import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Building2, LineChart, Bot, ShoppingBag, Check, MapPin, Mail, Rocket, Landmark, Globe, ShieldCheck, Wallet } from "lucide-react";
import { SITE } from "@/config";
import SeatsBadge from "@/components/SeatsBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#D9A441";
const TEAL = "#5EC8BE";

const serviceDefs = [
  { icon: Building2, id: "re", interest: "Real Estate" },
  { icon: LineChart, id: "dm", interest: "Digital Marketing" },
  { icon: Bot, id: "ai", interest: "AI Automation", badge: true },
  { icon: ShoppingBag, id: "ec", interest: "E-commerce" },
];

const principleDefs = ["sales", "marketing", "ai", "business"];

/* ---------------- Reveal ---------------- */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-6 flex items-center gap-3">
    <span className="h-px w-8" style={{ background: ACCENT }} />
    {children}
  </p>
);

/* ---------------- About ---------------- */
const About = () => {
  const { t } = useTranslation();
  return (
    <section id="about" data-testid="about-section" className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40 border-t hairline">
      <Reveal><Overline>{t("about.eyebrow")}</Overline></Reveal>
      <div className="grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-5">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">{t("about.title")}</h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7 space-y-6 text-lg text-neutral-400 leading-relaxed">
          <p>{t("about.p1")}</p>
          <p>{t("about.p2")}</p>
          <p className="text-neutral-200">{t("about.p3")}</p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
const Services = () => {
  const { t } = useTranslation();
  return (
    <section id="work" data-testid="services-section" className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40 border-t hairline">
      <Reveal><Overline>{t("work.eyebrow")}</Overline></Reveal>
      <Reveal>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">{t("work.title")}</h2>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-px bg-white/[0.06] mt-16 rounded-2xl overflow-hidden border hairline">
        {serviceDefs.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.id} delay={i * 0.06}>
              <a href="#contact" data-testid={`service-${i}`} className="group flex flex-col h-full bg-[#0B0B0D] p-8 md:p-10 hover:bg-[#101012] transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <Icon size={22} className="text-neutral-400 group-hover:text-white transition-colors duration-300" />
                  {s.badge && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-neutral-400">
                      <span className="h-1 w-1 rounded-full" style={{ background: ACCENT }} />
                      {t("work.ai.badge")}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mt-6 mb-2">{t(`work.${s.id}.label`)}</h3>
                <p className="text-neutral-400 leading-relaxed flex-1">{t(`work.${s.id}.line`)}</p>
                <span className="mt-8 inline-flex items-center gap-1.5 text-sm text-neutral-400 group-hover:text-white transition-colors duration-300">
                  {t("work.cardCta")}
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300 rtl:rotate-180" />
                </span>
              </a>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

/* ---------------- Principles ---------------- */
const Principles = () => {
  const { t } = useTranslation();
  return (
    <section id="principles" data-testid="principles-section" className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40 border-t hairline">
      <Reveal><Overline>{t("principles.eyebrow")}</Overline></Reveal>
      <div className="grid sm:grid-cols-2 gap-x-16 gap-y-14 mt-8">
        {principleDefs.map((id, i) => (
          <Reveal key={id} delay={i * 0.08}>
            <div className="border-t hairline pt-6">
              <p className="text-xs uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{t(`principles.${id}`)}</p>
              <p className="font-serif text-3xl md:text-4xl mt-4 leading-snug">{t(`principles.${id}_t`)}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Vision ---------------- */
const Vision = () => {
  const { t } = useTranslation();
  return (
    <section id="vision" data-testid="vision-section" className="border-t hairline">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border hairline px-3 py-1 text-xs uppercase tracking-[0.2em] text-neutral-400">
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: ACCENT }} />
            {t("vision.badge")}
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl font-bold tracking-[-0.02em] mt-8 leading-tight max-w-3xl">{t("vision.title")}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl">{t("vision.p")}</p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- INVEST (product intro) — sister to BUILD ---------------- */
const investBadges = ["Minimum Investment ₹25 Lakhs", "Global Investors Welcome", "Private Consultation"];
const investTrust = [
  { icon: Wallet, t: "Multi-Currency Investment Planner" },
  { icon: Globe, t: "Indian Residents & NRIs" },
  { icon: ShieldCheck, t: "Professional Documentation" },
  { icon: Check, t: "Personalized Consultation" },
];
const InvestSection = () => (
  <section id="invest" data-testid="home-invest-section" className="border-t hairline">
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40">
      <Reveal><Overline>Invest</Overline></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-4xl md:text-6xl font-bold tracking-[-0.02em] leading-tight max-w-3xl">Build Wealth Through Real Estate.</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl">
          Explore professionally structured real estate investment opportunities associated with established Indian real estate developers. Whether you're investing ₹25 Lakhs or ₹25 Crores+, discover opportunities designed for investors seeking regular income potential and long-term wealth creation.
        </p>
      </Reveal>

      {/* Featured card */}
      <Reveal delay={0.15}>
        <div
          className="mt-14 rounded-3xl border hairline overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(94,200,190,0.06), rgba(217,164,65,0.05))" }}
        >
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-3 p-8 md:p-12">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]" style={{ color: ACCENT }}>
                <Landmark size={15} /> Investment Planner
              </span>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight mt-4 leading-tight">Plan your investment in your own currency.</h3>
              <p className="mt-5 text-neutral-300 leading-relaxed">
                Plan your investment, explore illustrative monthly income estimates and connect with an advisor to discuss opportunities aligned with your financial goals.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-7">
                {investBadges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 rounded-full border hairline bg-black/30 px-3 py-1 text-xs text-neutral-300">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
                    {b}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-9">
                <Link
                  to="/invest"
                  data-testid="home-plan-invest"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-medium px-7 py-3.5 hover:bg-neutral-200 transition-colors duration-300"
                >
                  <LineChart size={16} /> Plan Your Investment
                </Link>
                <Link
                  to="/invest"
                  data-testid="home-advisor-invest"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border hairline text-sm font-medium px-7 py-3.5 text-neutral-200 hover:border-white/40 hover:text-white transition-colors duration-300"
                >
                  Speak With An Advisor <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform duration-300 rtl:rotate-180" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l hairline p-8 md:p-10 flex flex-col justify-center gap-5">
              {investTrust.map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.t} className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl border hairline bg-black/30" style={{ color: ACCENT }}>
                      <Icon size={17} strokeWidth={1.5} />
                    </span>
                    <p className="text-sm text-neutral-300">{it.t}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- BUILD (product intro) ---------------- */
const homeBuildBadges = ["Applications Open", "Beginner Friendly", "Live Guided Build"];
const BuildSection = () => (
  <section id="build" data-testid="home-build-section" className="border-t hairline">
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40">
      <Reveal><Overline>Build</Overline></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-4xl md:text-6xl font-bold tracking-[-0.02em] leading-tight max-w-3xl">Learn AI by Building Real Applications.</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-8 space-y-4 text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl">
          <p>BUILD is a live, hands-on learning experience where you’ll create real AI applications from scratch alongside me.</p>
          <p>Instead of spending hours watching tutorials, you’ll learn AI by building practical solutions that solve real business problems.</p>
          <p className="text-neutral-200">Every BUILD focuses on one application, one workflow, and one outcome. No coding experience required.</p>
        </div>
      </Reveal>

      {/* Featured card */}
      <Reveal delay={0.15}>
        <div
          className="mt-14 rounded-3xl border hairline overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(94,200,190,0.06), rgba(217,164,65,0.05))" }}
        >
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-3 p-8 md:p-12">
              <span className="text-xs uppercase tracking-[0.28em]" style={{ color: ACCENT }}>BUILD // 001</span>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight mt-4 leading-tight">Build Your First AI Voice Agent</h3>
              <p className="mt-5 text-neutral-300 leading-relaxed">
                Learn how modern AI voice agents are built using today’s AI tools. You’ll create your own AI Voice Agent that can present your business using your own script, ask qualification questions, capture responses, and send customer information back to your CRM.
              </p>
              <p className="mt-3 text-neutral-400 leading-relaxed">
                This Build is designed to teach you the complete workflow behind creating AI applications—not just copying prompts.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-7">
                <SeatsBadge />
                {homeBuildBadges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 rounded-full border hairline bg-black/30 px-3 py-1 text-xs text-neutral-300">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: TEAL }} />
                    {b}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-9">
                <Link
                  to="/build"
                  data-testid="home-explore-build"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-medium px-7 py-3.5 hover:bg-neutral-200 transition-colors duration-300"
                >
                  <Rocket size={16} /> Explore BUILD
                </Link>
                <Link
                  to="/build"
                  data-testid="home-apply-build"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border hairline text-sm font-medium px-7 py-3.5 text-neutral-200 hover:border-white/40 hover:text-white transition-colors duration-300"
                >
                  Applications Open <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform duration-300 rtl:rotate-180" />
                </Link>
              </div>
            </div>
            <Link
              to="/build"
              data-testid="home-build-image-link"
              aria-label="Explore BUILD // 001 — Build your first AI Voice Agent"
              className="lg:col-span-2 border-t lg:border-t-0 lg:border-l hairline bg-black flex items-center justify-center group cursor-pointer"
            >
              <picture>
                <source srcSet="/build-001.webp" type="image/webp" />
                <img
                  src="/build-001.jpg"
                  alt="BUILD // 001 — Build your first AI Voice Agent, live and guided"
                  width="1000"
                  height="1250"
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </picture>
            </Link>
          </div>
        </div>
      </Reveal>

      {/* Coming soon */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {["002", "003", "004"].map((n, i) => (
          <Reveal key={n} delay={0.1 + i * 0.06}>
            <div className="rounded-2xl border hairline bg-white/[0.03] p-6 h-full">
              <span className="text-xs uppercase tracking-[0.28em] text-neutral-500">BUILD // {n}</span>
              <p className="mt-3 text-lg font-medium text-neutral-400">Coming Soon</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-sm text-neutral-500 max-w-2xl">
        More Builds are coming soon. Every Build focuses on solving one real business problem using AI.
      </p>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp: "", email: "", interest: serviceDefs[0].interest, query: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success(t("toast.success"));
      setForm({ name: "", phone: "", whatsapp: "", email: "", interest: serviceDefs[0].interest, query: "" });
    } catch {
      toast.error(t("toast.error"));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { name: "name", label: t("contact.f_name"), type: "text", ph: t("contact.ph_name") },
    { name: "phone", label: t("contact.f_phone"), type: "tel", ph: SITE.phonePlaceholder },
    { name: "whatsapp", label: t("contact.f_whatsapp"), type: "tel", ph: SITE.phonePlaceholder },
    { name: "email", label: t("contact.f_email"), type: "email", ph: t("contact.ph_email") },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="border-t hairline">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-28 md:py-40">
        <Reveal>
          <p className="flex items-center gap-2 text-sm text-neutral-500 mb-10">
            <MapPin size={15} style={{ color: ACCENT }} />
            {t("contact.availability", { city: SITE.city })}
          </p>
          <Overline>{t("contact.eyebrow")}</Overline>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight max-w-xl">{t("contact.title")}</h2>
          <p className="mt-5 text-lg text-neutral-400 max-w-lg">{t("contact.p")}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          {done ? (
            <div data-testid="form-success" className="text-center border hairline rounded-2xl py-16">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-5 text-black" style={{ background: ACCENT }}>
                <Check size={24} />
              </span>
              <p className="text-2xl font-semibold">{t("contact.success_t")}</p>
              <p className="text-neutral-400 mt-2">{t("contact.success_p")}</p>
              <button data-testid="send-another-btn" onClick={() => setDone(false)}
                className="mt-6 text-sm text-neutral-400 underline underline-offset-4 hover:text-white transition-colors duration-300">
                {t("contact.another")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="lead-form" className="space-y-10">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4">{t("contact.interestLabel")}</label>
                <div className="flex flex-wrap gap-2.5">
                  {serviceDefs.map((s) => {
                    const active = form.interest === s.interest;
                    return (
                      <button
                        type="button"
                        key={s.id}
                        data-testid={`interest-${s.id}`}
                        onClick={() => setForm((f) => ({ ...f, interest: s.interest }))}
                        className={`rounded-full px-4 py-2 text-sm border transition-colors duration-300 ${
                          active ? "bg-white text-black border-transparent" : "text-neutral-300 hairline border hover:border-white/30"
                        }`}
                      >
                        {t(`work.${s.id}.label`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                {fields.map((f) => (
                  <div key={f.name}>
                    <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2.5">{f.label}</label>
                    <input
                      data-testid={`input-${f.name}`}
                      name={f.name}
                      type={f.type}
                      required
                      value={form[f.name]}
                      onChange={update}
                      placeholder={f.ph}
                      className="w-full bg-transparent border-b hairline border-b-white/15 py-2.5 text-lg placeholder:text-neutral-600 focus:border-b-white outline-none transition-colors duration-300"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2.5">{t("contact.f_message")}</label>
                <textarea
                  data-testid="input-query"
                  name="query"
                  required
                  rows={3}
                  value={form.query}
                  onChange={update}
                  placeholder={t("contact.ph_message")}
                  className="w-full bg-transparent border-b hairline border-b-white/15 py-2.5 text-lg placeholder:text-neutral-600 focus:border-b-white outline-none transition-colors duration-300 resize-none"
                />
              </div>

              <div className="flex pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-lead-btn"
                  className="group flex items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-medium px-8 py-4 hover:bg-neutral-200 transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? t("contact.sending") : t("contact.submit")}
                  {!loading && <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform duration-300 rtl:rotate-180" />}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer data-testid="footer" className="border-t hairline">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <p className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl text-neutral-200">{t("footer.line")}</p>
        <div className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-6 border-t hairline pt-8">
          <div>
            <p className="font-semibold">Anubhav Sahni</p>
            <p className="text-sm text-neutral-500 mt-1">© {new Date().getFullYear()} · {SITE.city} · {t("footer.rights")}</p>
          </div>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="footer-email"
            aria-label={`Email ${SITE.email}`}
            className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors duration-300"
          >
            <Mail size={16} />
            {SITE.email}
          </a>
        </div>
      </div>
    </footer>
  );
};

/* Below-the-fold sections, lazy-loaded to keep the initial bundle small. */
export default function BelowFold() {
  return (
    <>
      <About />
      <Services />
      <Principles />
      <Vision />
      <InvestSection />
      <BuildSection />
      <Contact />
      <Footer />
    </>
  );
}
